import { CookieSettingsLink } from "@/components/CookieConsent";
import { Link } from "@/lib/router-compat";
import {
  Activity,
  BookOpen,
  Bot,
  Briefcase,
  Building2,
  Calculator,
  Compass,
  DoorOpen,
  FileText,
  GraduationCap,
  Handshake,
  Instagram,
  Landmark,
  Linkedin,
  Mail,
  MapPin,
  MessageCircleQuestion,
  StickyNote,
  Store,
  Tag,
  Target,
  Thermometer,
  TrendingDown,
  Video,
  type LucideIcon,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import MarkerHighlight from "@/components/MarkerHighlight";

const Footer = () => {
  return <PanelLegalFooter />;
};

interface CorporateLink {
  label: string;
  to: string;
  icon: LucideIcon;
  emphasized?: boolean;
}

const FooterLinkList = ({ links }: { links: CorporateLink[] }) => (
  <ul className="space-y-1 font-body text-[13px] sm:space-y-2.5 sm:text-sm">
    {links.map(({ label, to, icon: Icon, emphasized }) => (
      <li key={`${to}-${label}`}>
        <Link
          to={to}
          onClick={() => window.scrollTo(0, 0)}
          className={`inline-flex min-h-[36px] items-start gap-2 py-1 leading-snug transition-colors hover:text-foreground sm:min-h-0 sm:items-center sm:py-0 ${emphasized ? "font-semibold text-foreground" : "text-muted-foreground"}`}
        >
          <Icon className="mt-0.5 h-4 w-4 shrink-0 sm:mt-0" strokeWidth={1.5} aria-hidden="true" />
          {label}
        </Link>
      </li>
    ))}
  </ul>
);

/** Footer corporativo completo, reservado a las Home pública ES/EN. */
export const HomeCorporateFooter = ({ dark = false }: { dark?: boolean } = {}) => {
  const { t, lang } = useLanguage();
  const es = lang === "es";
  const homeRoute = es ? "/" : "/en";

  // Orden por jerarquía de producto: Club Pro (tarifas) y agente primero;
  // la mentoría cierra la columna, nunca por delante del producto retail.
  const explore: CorporateLink[] = [
    { label: t("footerPublic", "pricing"), to: t("routes", "pricing"), icon: Tag },
    { label: t("footerPublic", "aiAgent"), to: t("routes", "aiAgentInfo"), icon: Bot },
    { label: t("footerPublic", "howItWorks"), to: t("routes", "howItWorks"), icon: Compass },
    { label: "Insights", to: t("routes", "insights"), icon: BookOpen },
    { label: es ? "Consejos e ideas" : "Tips and ideas", to: t("routes", "tips"), icon: StickyNote },
    { label: es ? "Casos reales" : "Real cases", to: t("routes", "cases"), icon: Video },
    { label: t("footerPublic", "resolvedQueries"), to: t("routes", "answers"), icon: MessageCircleQuestion },
    { label: t("footerPublic", "retailExperts"), to: t("routes", "retailExperts"), icon: Handshake },
    { label: t("footerPublic", "strategy"), to: t("routes", "strategy"), icon: Target },
    { label: t("footerPublic", "vipMentoring"), to: t("routes", "mentoring"), icon: GraduationCap },
  ];
  const services: CorporateLink[] = [
    { label: t("footerPublic", "everythingForShop"), to: t("routes", "everythingForShop"), icon: Store },
    { label: t("footerPublic", "storeProblems"), to: t("routes", "storeProblems"), icon: TrendingDown },
    { label: t("footerPublic", "openStoreSpain"), to: t("routes", "openStoreSpain"), icon: DoorOpen },
  ];
  const tools: CorporateLink[] = [
    { label: t("footerPublic", "retailCalculator"), to: t("routes", "calcPublic"), icon: Calculator },
    { label: t("footerPublic", "locationEvaluator"), to: es ? "/herramientas/mis-locales" : "/en/tools/my-premises", icon: MapPin },
    { label: t("footerPublic", "budgets"), to: es ? "/herramientas/proveedores" : "/en/tools/suppliers", icon: FileText },
    { label: t("footerPublic", "lemonThermometer"), to: t("routes", "thermometer"), icon: Thermometer },
    { label: t("footerPublic", "survivalTest"), to: t("routes", "survivalTest"), icon: Activity },
    { label: t("grants", "navLabel"), to: t("routes", "grants"), icon: Landmark, emphasized: true },
  ];
  const corporate: CorporateLink[] = [
    { label: t("footerPublic", "chainsFranchises"), to: t("routes", "chains"), icon: Building2 },
    { label: t("footerPublic", "familyOffices"), to: t("routes", "familyOffices"), icon: Briefcase },
  ];
  const contact: CorporateLink[] = [
    { label: t("footerPublic", "faq"), to: t("routes", "faq"), icon: MessageCircleQuestion },
    { label: t("footerPublic", "contactForm"), to: t("routes", "contact"), icon: Mail },
  ];

  return (
    <>
      {!dark && <div className="h-px w-full bg-foreground/15" />}
      <footer className={dark ? "footer-dark bg-footer-dark pt-16 text-footer-dark-foreground sm:pt-20" : "bg-secondary/60 pt-16 sm:pt-20"}>
        <section className="container max-w-6xl px-5 sm:px-6">
          <div className="grid grid-cols-2 gap-x-5 gap-y-10 sm:gap-x-8 lg:grid-cols-4 lg:gap-x-10">
            <div className="col-span-2 flex flex-col items-start space-y-2 border-b border-foreground/10 pb-8 sm:col-span-2 lg:col-span-1 lg:border-0 lg:pb-0">
              <Link
                to={homeRoute}
                onClick={() => window.scrollTo(0, 0)}
                className="font-display text-3xl font-bold tracking-tight transition-opacity hover:opacity-80"
              >
                RETAIL<MarkerHighlight height="0.78em" className={`px-[0.06em] ${dark ? "text-primary-foreground" : "text-foreground"}`}>LEMON</MarkerHighlight>
              </Link>
              <p className="font-body text-sm font-medium leading-snug text-muted-foreground">
                Managing Retail, Made Simple.
              </p>
              <p className="font-display text-sm italic leading-snug text-muted-foreground">
                {t("footerPublic", "missionPhrase")}
              </p>
            </div>

            <div>
              <p className="mb-4 font-body text-xs font-semibold uppercase tracking-[0.2em]">
                {t("footerPublic", "explore")}
              </p>
              <FooterLinkList links={explore} />
            </div>

            <div>
              <p className="mb-4 font-body text-xs font-semibold uppercase tracking-[0.2em]">
                {t("footerPublic", "retailServices")}
              </p>
              <FooterLinkList links={services} />
              <p className="mb-4 mt-10 font-body text-xs font-semibold uppercase tracking-[0.2em]">
                {t("footerPublic", "tools")}
              </p>
              <FooterLinkList links={tools} />
            </div>

            <div className="col-span-2 grid grid-cols-2 gap-x-5 border-t border-foreground/10 pt-8 sm:gap-x-8 lg:col-span-1 lg:block lg:border-0 lg:pt-0">
              <div>
              <p className="mb-4 font-body text-xs font-semibold uppercase tracking-[0.2em]">
                {t("footerPublic", "corporateSolutions")}
              </p>
              <FooterLinkList links={corporate} />
              </div>
              <div>
              <p className="mb-4 font-body text-xs font-semibold uppercase tracking-[0.2em] lg:mt-10">
                {t("footerPublic", "contact")}
              </p>
              <FooterLinkList links={contact} />
              <div className="mt-5 flex gap-4">
                <a
                  href="https://www.linkedin.com/company/retail-lemon/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={es ? "Retail Lemon en LinkedIn, se abre en una pestaña nueva" : "Retail Lemon on LinkedIn, opens in a new tab"}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Linkedin className="h-[18px] w-[18px]" strokeWidth={1.5} aria-hidden="true" />
                </a>
                <a
                  href="https://www.instagram.com/retaillemon/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={es ? "Retail Lemon en Instagram, se abre en una pestaña nueva" : "Retail Lemon on Instagram, opens in a new tab"}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Instagram className="h-[18px] w-[18px]" strokeWidth={1.5} aria-hidden="true" />
                </a>
              </div>
              </div>
            </div>
          </div>
        </section>

        <div className="mt-16 border-t border-foreground/8">
          <LegalBar />
        </div>
      </footer>
    </>
  );
};

/** Franja legal: la única parte del footer que se muestra en las vistas internas del panel. */
const LegalBar = () => {
  const { t } = useLanguage();
  return (
    <section className="container max-w-6xl py-4 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-[11px] text-muted-foreground font-body">
      <span>{t("footerPublic", "rights")}</span>
      <span className="hidden sm:inline">|</span>
      <Link to={t("routes", "legal")} className="hover:text-foreground transition-colors">{t("footerPublic", "legalNotice")}</Link>
      <span>|</span>
      <Link to={t("routes", "privacy")} className="hover:text-foreground transition-colors">{t("footerPublic", "privacy")}</Link>
      <span>|</span>
      <Link to={t("routes", "cookies")} className="hover:text-foreground transition-colors">{t("footerPublic", "cookies")}</Link>
      <span>|</span>
      <CookieSettingsLink />
      <span>|</span>
      <Link to={t("routes", "terms")} className="hover:text-foreground transition-colors">{t("footerPublic", "terms")}</Link>
      <span>|</span>
      <Link to={t("routes", "faq")} className="hover:text-foreground transition-colors">FAQ</Link>
      <span>|</span>
      <Link to={t("routes", "partners")} rel="nofollow" className="hover:text-foreground transition-colors">{t("footerPublic", "partnersAccess")}</Link>
    </section>
  );
};

/** Pie compacto para las vistas internas del panel (herramientas, insights, casos…). */
export const PanelLegalFooter = () => (
  <footer className="mt-auto border-t border-foreground/8 bg-background">
    <LegalBar />
  </footer>
);

export default Footer;
